import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { IPromosEcommerce } from '../../../private/shared/models/productos';
import { AnalyticsEventType } from '../../enums/analytics.enum';
import { IAnalyticsListContext, IAvanceFavoritosEvent, IFinalizadoFavoritosEvent, IMapProductParams, IProductItem, ISendAddToCartParams, ISendAvanceFavoritosParams, ISendBeginCheckoutParams, ISendFinalizadoFavoritosParams, ISendPurchaseParams, ISendRefundParams, ISendRemoveFromCartParams, ISendViewCartParams, ISendViewItemListParams, PromoImpactMap, StoredOriginsMap } from '../../models/Analytics';
import { NativeStorageNamesV2, SECTIONS } from '../../models/Constants';
import { MonitoringService } from '../../services/monitoring/monitoring.service';
import { StorageData } from '../../services/utilities/storage-data.class';
import { MainAnalyticsService } from '../main-analytics/main-analytics.service';
import { UserAnalyticsService } from '../user-analytics/user-analytics.service';

@Injectable({
  providedIn: 'root'
})
export class EcommerceAnalyticsService {
  constructor(
    private mainAnalyticsS: MainAnalyticsService,
    private userAnalyticsS: UserAnalyticsService,
    private monitoringS: MonitoringService
  ) { }

  /**
   * Guarda o actualiza los orígenes de atribución de productos y promociones en el almacenamiento local.
   */
  public async saveProductOrigin(
    itemId: string,
    clavePromo: string,
    listId: string,
    listName: string,
    overwrite: boolean = true
  ): Promise<void> {
    await this.saveProductOrigins([{ itemId, clavePromo }], listId, listName, overwrite);
  }

  /**
   * Guarda o actualiza múltiples orígenes de atribución en una sola operación de almacenamiento.
   */
  public async saveProductOrigins(
    items: Array<{ itemId: string; clavePromo: string }>,
    listId: string,
    listName: string,
    overwrite: boolean = true
  ): Promise<void> {
    if (!items || items.length === 0) {
      return;
    }
    try {
      const existing: StoredOriginsMap = await StorageData.getDataStorage(NativeStorageNamesV2.LastAnalyticsOrigin) || {};
      let hasChanges = false;
      for (const item of items) {
        const key = `${item.itemId}::${item.clavePromo || 'null'}`;
        if (overwrite || !existing[key]) {
          existing[key] = {
            item_list_id: listId,
            item_list_name: listName
          };
          hasChanges = true;
        }
      }
      if (hasChanges) {
        await StorageData.setDataStorage(NativeStorageNamesV2.LastAnalyticsOrigin, existing);
      }
    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

  /**
   * Obtiene todos los orígenes de atribución almacenados localmente.
   */
  public async getProductOrigins(): Promise<StoredOriginsMap> {
    try {
      return await StorageData.getDataStorage<StoredOriginsMap>(NativeStorageNamesV2.LastAnalyticsOrigin) || {};
    } catch (err) {
      this.monitoringS.captureException(err);
      return {};
    }
  }

  /**
   * Re-atribuye de manera asíncrona las propiedades item_list_id e item_list_name
   * de una lista de ítems de analíticas utilizando los orígenes guardados en el storage
   * o aplicando las reglas de fallback especificadas.
   */
  public async enrichItemsWithOrigins(items: IProductItem[]): Promise<IProductItem[]> {
    if (!items || items.length === 0) {
      return items;
    }
    try {
      const origins = await this.getProductOrigins();
      return items.map(item => {
        const key = `${item.item_id}::${item.id_promocion || 'null'}`;
        if (origins[key]) {
          return {
            ...item,
            item_list_id: origins[key].item_list_id,
            item_list_name: origins[key].item_list_name
          };
        } else {
          // Aplicar reglas de fallback
          const cat5 = (item.item_category5 || '').trim().toUpperCase();
          let resolvedListId: string;
          let resolvedListName: string;

          switch (cat5) {
            case 'DINAMICO':
              resolvedListId = 'promocion_dinamica';
              resolvedListName = 'Home::Promociones::Lista_Principal';
              break;
            case 'ESCALERA':
              resolvedListId = 'promocion_escalera';
              resolvedListName = 'Home::Promociones::Lista_Principal';
              break;
            default:
              if (item.modelo === 6) {
                resolvedListId = 'favoritos';
                resolvedListName = 'Home::Favoritos::Lista_Principal';
              } else if (item.modelo === 7) {
                resolvedListId = 'tendencias';
                resolvedListName = 'Home::Tendencias::Lista_Principal';
              } else if (item.id_promocion && item.id_promocion !== '') {
                resolvedListId = 'promociones';
                resolvedListName = 'Home::Promociones::Lista_Principal';
              } else {
                resolvedListId = 'categorias';
                resolvedListName = 'Home::Categorias::Lista_Principal';
              }
          }

          return {
            ...item,
            item_list_id: resolvedListId,
            item_list_name: resolvedListName
          };
        }
      });
    } catch (err) {
      this.monitoringS.captureException(err);
      return items;
    }
  }

  /**
   * Resuelve el contexto analítico de lista (listId, listName, section)
   * aplicando valores por defecto según el tipo de promoción si no se proporciona un contexto explícito.
   */
  public resolvePromoAnalyticsContext(
    context?: Partial<IAnalyticsListContext>,
    promoType?: 'ESCALERA' | 'DINAMICO' | 'BONIFICACION' | string
  ): IAnalyticsListContext {
    if (context && context.listId && context.listName) {
      return {
        listId: context.listId,
        listName: context.listName,
        section: context.section || 'Tienda'
      };
    }

    const typeUpper = (promoType || '').toUpperCase();
    switch (typeUpper) {
      case 'ESCALERA':
        return {
          listId: 'promocion_escalera',
          listName: 'Home::Promociones::Lista_Principal',
          section: 'Tienda'
        };
      case 'DINAMICO':
      case 'BONIFICACION':
      default:
        return {
          listId: 'promocion_dinamica',
          listName: 'Home::Promociones::Lista_Principal',
          section: 'Tienda'
        };
    }
  }

  /**
   * Construye un mapa de ClavePromo -> { MaximoPaquetes, PaquetesCanjeados } desde un array de promociones.
   */
  public buildPromoImpactMap(promos: IPromosEcommerce[]): PromoImpactMap {
    const map: PromoImpactMap = {};
    for (const promo of promos) {
      if (promo.ClavePromo) {
        map[promo.ClavePromo] = {
          MaximoPaquetes: promo.MaximoPaquetes,
          PaquetesCanjeados: promo.PaquetesCanjeados,
          Vigencia: promo.Vigencia,
          TipoPromocion: promo.Bonificacion ? 'BONIFICACION' : promo.TipoPromocion,
          puntos_recompepsi: promo.puntos_recompepsi
        };
      }
    }
    return map;
  }

  /**
   * Convierte productos del negocio al formato unificado de analíticas.
   */
  public mapProductToAnalyticsItem(params: IMapProductParams): IProductItem {
    const { product, index, listId, listName, promoInfo, includePoints = true } = params;
    const price = product.precio || 0;
    const discount = product.precio_original ? (product.precio_original - price) : 0;
    const clavePromo = product.ClavePromo || '';
    const isPromo = clavePromo !== '';
    let impactos_disponibles: number | string = '';
    let impactos_agotados: string = '';
    let promo_termina_en: string = '';
    let item_category5: string = '';

    if (isPromo) {
      if ((product as any).Bonificacion) {
        item_category5 = 'BONIFICACION';
      } else if (promoInfo && promoInfo.TipoPromocion) {
        item_category5 = promoInfo.TipoPromocion;
      } else if ((product as any).TipoPromocion) {
        item_category5 = (product as any).TipoPromocion;
      }

      if (promoInfo) {
        impactos_disponibles = promoInfo.MaximoPaquetes - promoInfo.PaquetesCanjeados;
        impactos_agotados = impactos_disponibles > 0 ? 'no' : 'si';
        const vigencia = promoInfo.Vigencia;
        promo_termina_en = vigencia
          ? vigencia === 1 ? '1 dia' : `${vigencia} dias`
          : '';
      }
    }

    const item: IProductItem = {
      item_name: (product.title || '').toLowerCase(),
      item_id: String(product.clave_producto || product.id_producto || ''),
      price: product.precio || 0,
      item_brand: (product.marca || '').toLowerCase(),
      item_category: (product.familia || '').toLowerCase(),
      item_category2: (product.segmento || '').toLowerCase(),
      item_category3: `${(product.marca || '').toLowerCase()}::${(product.sku || '').toLowerCase()}`,
      item_category4: `${product.presentacion || ''}::${product.TipoEnvase || ''}::${product.Retornabilidad || ''}`,
      item_category5,
      item_variant: (product.subtitle || '').toLowerCase(),
      quantity: product.productos || 1,
      index: index + 1,
      item_list_id: listId,
      item_list_name: listName,
      item_packaging: product.TipoEnvase || '-',

      // Nuevas propiedades dinámicas
      discount: Number(discount.toFixed(2)),
      precio_sin_descuento: product.precio_original || product.precio || 0,
      promo_termina_en,
      impactos_disponibles,
      impactos_agotados,
      id_promocion: clavePromo,
      modelo: product.modelo,
    };

    if (includePoints) {
      item.puntos_por_compra = product.puntos_recompepsi ?? promoInfo?.puntos_recompepsi ?? 0;
    }

    return item;
  }

  /**
   * Calcula la sumatoria de (puntos_por_compra * quantity) de los items proporcionados.
   */
  public calculateTotalPoints(items: IProductItem[]): number {
    if (!items || items.length === 0) {
      return 0;
    }
    return items.reduce((total, item) => {
      const points = item.puntos_por_compra || 0;
      const quantity = item.quantity || 1;
      return total + (points * quantity);
    }, 0);
  }

  /**
   * Envía el evento 'view_item_list' a GTM y Firebase Analytics.
   */
  public async sendViewItemList(params: ISendViewItemListParams): Promise<void> {
    try {
      const { products, listId, listName, section, startIndex = 0, promoMap } = params;
      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      const allItems = products.map((prod, idx) => this.mapProductToAnalyticsItem({
        product: prod,
        index: startIndex + idx,
        listId,
        listName,
        promoInfo: promoMap?.[prod.ClavePromo || ''],
        includePoints: false
      }));
      const platformOrigin = await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin);
      const envAnalytics = (environment.enviromentAnalytics || 'dev').toLowerCase();

      const chunkSize = 25; // Tamaño máximo de items por evento
      const chunks: IProductItem[][] = [];
      for (let i = 0; i < allItems.length; i += chunkSize) {
        chunks.push(allItems.slice(i, i + chunkSize));
      }

      const eventPromises = chunks.map((items) => {
        const eventData = {
          event: 'view_item_list',
          currency: 'MXN',
          item_list_id: listId,
          item_list_name: listName,
          section,
          environment: envAnalytics,
          origin: platformOrigin || 'web',
          items
        };

        return Promise.all([
          this.mainAnalyticsS.sendEventGTM({ ...eventData, ...userProps }),
          this.mainAnalyticsS.logEventFirebase('view_item_list', eventData)
        ]);
      });

      await Promise.all(eventPromises);
    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

  /**
   * Envía el evento 'add_to_cart' al agregar productos.
   */
  public async sendAddToCart(params: ISendAddToCartParams): Promise<void> {
    try {
      const { products, value, section, listId, listName, promoMap } = params;

      // Almacenar o preservar el origen de atribución
      const sectionLower = section?.toLowerCase();
      const isCartSection = sectionLower === 'cart' || sectionLower === 'carrito';

      const itemsToSave = (products || [])
        .map(prod => ({
          itemId: String(prod.clave_producto || prod.id_producto || ''),
          clavePromo: prod.ClavePromo || ''
        }))
        .filter(item => item.itemId !== '');

      if (itemsToSave.length > 0) {
        // Si no es carrito, sobreescribe el origen. Si proviene del carrito, solo guarda si no existía previamente.
        await this.saveProductOrigins(itemsToSave, listId, listName, !isCartSection);
      }

      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      const items = products.map((prod, idx) => this.mapProductToAnalyticsItem({
        product: prod,
        index: idx,
        listId,
        listName,
        promoInfo: promoMap?.[prod.ClavePromo || ''],
        includePoints: true
      }));
      const platformOrigin = await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin);
      const total_puntos = this.calculateTotalPoints(items);

      const eventData = {
        event: 'add_to_cart',
        currency: 'MXN',
        value,
        total_puntos,
        section,
        environment: (environment.enviromentAnalytics || 'dev').toLowerCase(),
        origin: platformOrigin || 'web',
        items
      };

      await Promise.all([
        this.mainAnalyticsS.sendEventGTM({ ...eventData, ...userProps }),
        this.mainAnalyticsS.logEventFirebase('add_to_cart', eventData)
      ]);
    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

  /**
   * Envía el evento 'remove_from_cart' al eliminar/disminuir productos.
   */
  public async sendRemoveFromCart(params: ISendRemoveFromCartParams): Promise<void> {
    try {
      const { products, value, section, listId, listName, promoMap } = params;
      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      const items = products.map((prod, idx) => this.mapProductToAnalyticsItem({
        product: prod,
        index: idx,
        listId,
        listName,
        promoInfo: promoMap?.[prod.ClavePromo || ''],
        includePoints: true
      }));
      const platformOrigin = await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin);
      const total_puntos = this.calculateTotalPoints(items);

      const eventData = {
        event: 'remove_from_cart',
        currency: 'MXN',
        value,
        total_puntos,
        section,
        environment: (environment.enviromentAnalytics || 'dev').toLowerCase(),
        origin: platformOrigin || 'web',
        items
      };

      await Promise.all([
        this.mainAnalyticsS.sendEventGTM({ ...eventData, ...userProps }),
        this.mainAnalyticsS.logEventFirebase('remove_from_cart', eventData)
      ]);
    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

  /**
   * Envía el evento 'begin_checkout' al iniciar el pago.
   */
  public async sendBeginCheckout(params: ISendBeginCheckoutParams): Promise<void> {
    try {
      const { items, value, section } = params;
      const enrichedItems = await this.enrichItemsWithOrigins(items);
      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      const platformOrigin = await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin);
      const total_puntos = this.calculateTotalPoints(enrichedItems);

      const eventData = {
        event: 'begin_checkout',
        currency: 'MXN',
        value,
        total_puntos,
        section,
        environment: (environment.enviromentAnalytics || 'dev').toLowerCase(),
        origin: platformOrigin || 'web',
        items: enrichedItems
      };

      await Promise.all([
        this.mainAnalyticsS.sendEventGTM({ ...eventData, ...userProps }),
        this.mainAnalyticsS.logEventFirebase('begin_checkout', eventData)
      ]);
    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

  /**
   * Envía el evento 'view_cart' al visualizar el carrito.
   */
  public async sendViewCart(params: ISendViewCartParams): Promise<void> {
    try {
      const { items, value, section } = params;
      const enrichedItems = await this.enrichItemsWithOrigins(items);
      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      const platformOrigin = await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin);
      const total_puntos = this.calculateTotalPoints(enrichedItems);

      const eventData = {
        event: 'view_cart',
        currency: 'MXN',
        value,
        total_puntos,
        section,
        environment: (environment.enviromentAnalytics || 'dev').toLowerCase(),
        origin: platformOrigin || 'web',
        items: enrichedItems
      };

      await Promise.all([
        this.mainAnalyticsS.sendEventGTM({ ...eventData, ...userProps }),
        this.mainAnalyticsS.logEventFirebase('view_cart', eventData)
      ]);
    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

  /**
   * Envía el evento 'purchase' al confirmar la compra de forma exitosa.
   */
  public async sendPurchase(params: ISendPurchaseParams): Promise<void> {
    try {
      const {
        transactionId,
        value,
        items,
        coupon = '',
        shipping = 0,
        tax = 0.16,
        section = SECTIONS.CONFIRMACION
      } = params;
      const enrichedItems = await this.enrichItemsWithOrigins(items);
      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      const platformOrigin = await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin);
      const total_puntos = this.calculateTotalPoints(enrichedItems);

      const eventData = {
        event: 'purchase',
        currency: 'MXN',
        value,
        total_puntos,
        transaction_id: transactionId,
        coupon,
        shipping,
        tax,
        section,
        environment: (environment.enviromentAnalytics || 'dev').toLowerCase(),
        origin: platformOrigin || 'web',
        items: enrichedItems
      };

      await Promise.all([
        this.mainAnalyticsS.sendEventGTM({ ...eventData, ...userProps }),
        this.mainAnalyticsS.logEventFirebase('purchase', eventData)
      ]);
    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

  /**
   * Envía el evento 'refund' al realizar una devolución o editar pedidos reduciendo productos.
   */
  public async sendRefund(params: ISendRefundParams): Promise<void> {
    try {
      const { transactionId, value, items, section = 'Resumen Pedido' } = params;
      const enrichedItems = await this.enrichItemsWithOrigins(items);
      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      const platformOrigin = await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin);
      const total_puntos = this.calculateTotalPoints(enrichedItems);

      const eventData = {
        event: 'refund',
        currency: 'MXN',
        value,
        total_puntos,
        transaction_id: transactionId,
        section,
        environment: (environment.enviromentAnalytics || 'dev').toLowerCase(),
        origin: platformOrigin || 'web',
        items: enrichedItems
      };

      await Promise.all([
        this.mainAnalyticsS.sendEventGTM({ ...eventData, ...userProps }),
        this.mainAnalyticsS.logEventFirebase('refund', eventData)
      ]);
    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

  /**
   * Emite el evento analítico 'avance_favoritos' hacia GTM y Firebase
   * incluyendo los parámetros del reto y las propiedades globales del usuario.
   */
  public async sendAvanceFavoritos(params: ISendAvanceFavoritosParams): Promise<void> {
    try {
      const {
        meta_propuesta = 0,
        avance = 0,
        termina_en = '',
        puntos_a_ganar = 0,
        section = SECTIONS.TIENDA
      } = params;

      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      const platformOrigin = (await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin)) || 'web';
      const envAnalytics = (environment.enviromentAnalytics || 'dev').toLowerCase();

      const eventData: IAvanceFavoritosEvent = {
        event: AnalyticsEventType.AVANCE_FAVORITOS,
        origin: platformOrigin,
        section,
        environment: envAnalytics,
        meta_propuesta,
        avance,
        termina_en,
        puntos_a_ganar
      };

      await Promise.all([
        this.mainAnalyticsS.sendEventGTM({ ...eventData, ...userProps }),
        this.mainAnalyticsS.logEventFirebase(eventData.event, eventData as any)
      ]);
    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

  /**
   * Emite el evento analítico 'finalizado_favoritos' hacia GTM y Firebase
   * cuando un reto de favoritos finaliza (completado o expirado).
   *
   * Aplica control de unicidad estricto: solo se dispara 1 sola vez por usuario
   * y por ciclo de reto finalizado (persistido en Storage).
   *
   * @param params Parámetros específicos del reto finalizado y datos del ciclo
   */
  public async sendFinalizadoFavoritos(params: ISendFinalizadoFavoritosParams): Promise<void> {
    try {
      const {
        meta_propuesta = 0,
        avance = 0,
        termina_en = '',
        estado = '',
        nuevo_reto_en = 0,
        puntos_recibidos = 0,
        section = SECTIONS.TIENDA,
        idPilar = 4,
        fechaInicio = '',
        fechaFin = '',
        estatus = 0
      } = params;

      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      const userId = userProps.user_id || 'anonymous';

      // 1. Clave de tracking unificada por usuario y pilar (mantiene un único registro por usuario)
      const userChallengeKey = `tracked_favs_finished_${userId}_${idPilar}`;
      const currentCycleSignature = `${fechaInicio}_${fechaFin}_${estatus}`;

      // 2. Validar si ya fue despachado previamente para este mismo ciclo
      const savedCycle = await StorageData.getDataStorage<string>(userChallengeKey);

      if (savedCycle === currentCycleSignature) {
        return; // Idempotencia: Ciclo actual ya emitido previamente
      }

      const platformOrigin = (await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin)) || 'web';
      const envAnalytics = (environment.enviromentAnalytics || 'dev').toLowerCase();

      const eventData: IFinalizadoFavoritosEvent = {
        event: AnalyticsEventType.FINALIZADO_FAVORITOS,
        origin: platformOrigin,
        section,
        environment: envAnalytics,
        meta_propuesta,
        avance_logrado: avance,
        termino_el: termina_en,
        estado,
        nuevo_reto_en,
        puntos_recibidos
      };

      await Promise.all([
        this.mainAnalyticsS.sendEventGTM({ ...eventData, ...userProps }),
        this.mainAnalyticsS.logEventFirebase(eventData.event, eventData as any)
      ]);

      // 3. Persistir/actualizar la firma del nuevo ciclo (sobrescribe cualquier ciclo previo del mismo usuario)
      await StorageData.setDataStorage(userChallengeKey, currentCycleSignature);

    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

}
